import type { LocaleCode } from '@ifixit/i18n/types';
import {
   parseUserLocale,
   routeLocaleByUserLocale,
   type UserLocaleCode,
} from '@ifixit/i18n/user-locale';
import { LocationProvider as SharedLocationProvider } from '@ifixit/ui/components/location-provider';
import { geoUserLocaleForPhpLangId } from '@ifixit/i18n/geo-user-locale';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Geo } from 'Shared/utils/geo';
import { OpenLocationPickerListener } from './open-location-picker';

interface LocationProviderProps {
   children: ReactNode;
   langLinks: Record<string, string>;
}

export function LocationProvider({ children, langLinks }: LocationProviderProps) {
   const { geoDetectedUserLocale, geoPending } = useGeoDetectedUserLocale(App.lang);

   return (
      <SharedLocationProvider
         currentPhpLangId={App.lang}
         geoDetectedUserLocale={geoDetectedUserLocale}
         geoPending={geoPending}
         hrefFromUserLocale={hrefFromUserLocale}
         demoteRouteLocale={demoteRouteLocale}
         isCarpenter
         withProgressBar={(callback: () => Promise<void>) => callback()}
      >
         <OpenLocationPickerListener />
         {children}
      </SharedLocationProvider>
   );

   function hrefFromUserLocale(userLocaleCode: UserLocaleCode) {
      const { language } = parseUserLocale(userLocaleCode);
      return langLinks[routeLocaleByUserLocale[userLocaleCode]] ?? langLinks[language];
   }
}

// Demote an unpublished route locale to its published fallback.
function demoteRouteLocale(code: LocaleCode): LocaleCode {
   return App.unpublishedRouteLocaleFallbacks?.[code] ?? code;
}

function useGeoDetectedUserLocale(phpLangId: string): {
   geoDetectedUserLocale: UserLocaleCode | null;
   geoPending: boolean;
} {
   const [countryCode, setCountryCode] = useState<string | null>(null);
   const [geoPending, setGeoPending] = useState(true);

   useEffect(() => {
      let active = true;
      Geo.locate()
         .then(geo => {
            if (active && geo.countryCode != null) {
               setCountryCode(geo.countryCode);
            }
         })
         .catch(() => {
            // Geo lookup failed; fall back to non-geo locale resolution.
         })
         .finally(() => {
            if (active) {
               setGeoPending(false);
            }
         });
      return () => {
         active = false;
      };
   }, []);

   const geoDetectedUserLocale = useMemo(
      () => geoUserLocaleForPhpLangId(countryCode, phpLangId),
      [countryCode, phpLangId]
   );

   return { geoDetectedUserLocale, geoPending };
}
